import type {JSX} from 'react';
import React, {useState} from 'react';
import {useTranslation} from 'next-i18next';
import SearchMode from '@deezer/next/shared/utils/SearchMode';
import {ContentType} from '@deezer/next/services/types/contentType';
import {ArrowLeftIcon, ArrowRightIcon} from '@tempo/icons';
import type {
	AlbumSearchResult,
	ArtistSearchResult,
	PlaylistSearchResult,
	SearchAllResult,
	TrackSearchResult,
} from '@deezer/next/services/api/Starmania/requests/search/types';
import {
	Box,
	Button,
	Heading,
	HStack,
	IconButton,
	SimpleGrid,
	Text,
	VStack,
} from '@tempo/core';
import ArtistSearchResultItem from '@components/Navigation/SearchBar/ArtistSearchResultItem';
import TrackSearchResultItem from '@components/Navigation/SearchBar/TrackSearchResultItem';
import AlbumSearchResultItem from '@components/Navigation/SearchBar/AlbumSearchResultItem';
import PlaylistSearchResultItem from '@components/Navigation/SearchBar/PlaylistSearchResultItem';

interface AutocompletePanelProps {
	data: SearchAllResult;
	searchMode: SearchMode;
	typeKey?: CategoryKey;
}

type CategoryKey = keyof SearchAllResult['data']['result'];

// playlists can be omitted for provider/label users
const categoryData = {
	albums: ContentType.Album,
	artists: ContentType.Artist,
	playlists: ContentType.Playlist,
	tracks: ContentType.Track,
};

// AutoComplete
const SearchResults = ({
	data,
	searchMode,
	typeKey,
}: AutocompletePanelProps): JSX.Element | null => {
	const {t} = useTranslation('d4c');
	const isTypedSearchMode = searchMode === SearchMode.editArtist;

	const getCategoryTitle = (typeCategory?: ContentType) => {
		switch (typeCategory) {
			case ContentType.Album:
				return t('artistedition_action_album_d4c-web');
			case ContentType.Artist:
				return t('analyticsmanager_text_artist_d4c-web');
			case ContentType.Playlist:
				return t('artistedition_action_playlist_d4c-web');
			case ContentType.Track:
				return t('playlistreport_text_track_d4c-web');
			default:
				return '';
		}
	};

	const renderSearchItem = (
		item:
			| ArtistSearchResult
			| TrackSearchResult
			| AlbumSearchResult
			| PlaylistSearchResult,
		categoryType: ContentType,
	) => {
		switch (categoryType) {
			case ContentType.Artist:
				return (
					<ArtistSearchResultItem
						key={`artist-${item.id}`}
						artist={item as ArtistSearchResult}
						query={data.data.query}
						searchMode={searchMode}
					/>
				);
			case ContentType.Track:
				return (
					<TrackSearchResultItem
						key={`track-${item.id}`}
						track={item as TrackSearchResult}
						query={data.data.query}
					/>
				);
			case ContentType.Album:
				return (
					<AlbumSearchResultItem
						key={`album-${item.id}`}
						album={item as AlbumSearchResult}
						query={data.data.query}
					/>
				);
			case ContentType.Playlist:
				return (
					<PlaylistSearchResultItem
						key={`playlist-${item.id}`}
						playlist={item as PlaylistSearchResult}
						query={data.data.query}
					/>
				);
			default:
				return null;
		}
	};

	const [viewAll, setViewAll] = useState<boolean>(isTypedSearchMode);
	const [forceViewAll] = useState<boolean>(isTypedSearchMode);
	const [viewAllKey, setViewAllKey] = useState<CategoryKey | undefined>(
		typeKey,
	);

	const toggleView = (key?: CategoryKey) => {
		setViewAll(!viewAll);
		setViewAllKey(key);
	};

	const renderViewAllButton = (key: CategoryKey) => {
		return (
			<Button
				size="small"
				variant="ghost"
				color="gray"
				onClick={() => {
					toggleView(key);
				}}
				rightIcon={<ArrowRightIcon />}
			>
				{t('generic_action_seeall_d4c-web')}
			</Button>
		);
	};

	const viewAllResults = viewAllKey ? (data.data.result[viewAllKey] ?? []) : [];

	return (
		<Box pt={4} overflow="hidden">
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'nowrap',
					transform: viewAll ? 'translateX(-50%)' : 'translateX(0)',
					transition: 'transform ease 500ms 100ms',
					width: '200%',
				}}
			>
				<SimpleGrid
					columns={{base: 1, sm: 2}}
					spacingX={8}
					sx={{
						flexBasis: '100%',
						maxHeight: '100%',
						transition: 'opacity ease 300ms',
						opacity: viewAll ? 0 : 1,
					}}
				>
					{Object.entries({...data.data?.result}).map(
						([key, searchData]: [
							string,
							(
								| ArtistSearchResult
								| TrackSearchResult
								| AlbumSearchResult
								| PlaylistSearchResult
							)[],
						]) => {
							const categoryType = categoryData[key as CategoryKey];
							return (
								<Box
									key={categoryType}
									data-testid={`search-section-${categoryType}`}
								>
									<HStack
										justifyContent="space-between"
										pb={2}
										pe={2}
										borderBottom="1px solid"
										borderBottomColor="divider.neutral.primary.default"
										height="33px"
									>
										<Heading
											as="h2"
											variant="title.section"
											color="text.neutral.secondary.default"
										>
											{getCategoryTitle(categoryType)}
										</Heading>
										{searchData.length > 2 &&
											renderViewAllButton(key as CategoryKey)}
									</HStack>
									<VStack alignItems="initial" spacing={4} py={4}>
										{searchData.length === 0 || searchData[0] === null ? (
											<Text
												fontStyle="italic"
												variant="body.small.regular"
												m={2}
												textAlign="center"
											>
												{t(
													'analyticshome_text_noresultincatalogforcategory_d4c-web',
												)}
											</Text>
										) : (
											searchData
												.slice(0, 2)
												.map(item => renderSearchItem(item, categoryType))
										)}
									</VStack>
								</Box>
							);
						},
					)}
				</SimpleGrid>
				<Box
					sx={{
						flexBasis: '100%',
						maxHeight: '100%',
						transition: 'opacity ease 300ms',
						opacity: viewAll ? 1 : 0,
					}}
				>
					{viewAll && viewAllKey && (
						<>
							<HStack
								pb={2}
								ps={2}
								borderBottom="1px solid"
								borderBottomColor="divider.neutral.primary.default"
								height="33px"
							>
								{!forceViewAll && (
									<IconButton
										variant="ghost"
										color="gray"
										size="small"
										aria-label={'back'}
										onClick={() => toggleView()}
										icon={<ArrowLeftIcon />}
									/>
								)}
								<Heading
									as="h2"
									variant="title.section"
									color="text.neutral.secondary.default"
								>
									{getCategoryTitle(categoryData[viewAllKey])}
								</Heading>
							</HStack>
							<SimpleGrid
								columns={{base: 1, sm: 2}}
								spacingX={8}
								spacingY={4}
								py={4}
							>
								{viewAllResults.length === 0 || viewAllResults[0] === null ? (
									<Text
										fontStyle="italic"
										variant="body.small.regular"
										m={2}
										textAlign="center"
									>
										{t(
											'analyticshome_text_noresultincatalogforcategory_d4c-web',
										)}
									</Text>
								) : (
									viewAllResults.map(item =>
										renderSearchItem(item, categoryData[viewAllKey]),
									)
								)}
							</SimpleGrid>
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export default SearchResults;
